import React, { useState } from 'react'
import {
  Button,
  Grid,
  Icon,
  Popup,
  Search,
  SearchResultData
} from 'semantic-ui-react'
import map from 'lodash/map'
import { AxiosResponse } from 'axios'
// Local
import {
  API_URL,
  shouldRenderReskin2025
} from '../../../constant'
import { api } from '../../../action/api'
import { searchGenerator } from '../../../action/search'
import {
  formatIcd10Code,
  rawIcd10Code
} from '../../common/formatUtils'
import Close from '../../icons/close'
import ReveleerButton from '../../common/ReveleerButton'

type SearchProps = {
  className: string
  name: string
  value: string
  loading: boolean
  placeholder: string
  results: any[]
  aligned: string
  icon: null
  fluid: boolean
  clear: boolean
  input: string
  minCharacters: number
  noResultsMessage: string
  showNoResults: boolean
}

type Result = {
  title: string
  description: string
}

type APIResult = {
  id: string
  description: string
}

type SearchResultInternal = {
  className: string
  name: string
  value: string
  loading: boolean
  placeholder: string
  results: Result[]
  aligned: string
  icon: null
  fluid: boolean
  clear: string
  input: string
  minCharacters: number
  noResultsMessage: string
  showNoResults: boolean
  result: Result
}

/**
 * Provides an ICD10 search
 *
 * Notes:
 *
 * This is a surprisingly tricky component.
 *
 * this.state.value is either a formatted ICD10 code or search input
 *
 * We format ICD10 codes for display with a decimal and store them as raw.
 * Example: Formatted: A01.01, Raw: A0101
 *
 * @param name Component name. Default: icd10
 * @param value Initial ICD10 value
 * @param placeholder Place holder override. Default: ICD10
 * @param onSelect Callback function for ICD10 code select
 *        Arguments: Object: { id, description }, matching the storage model,
 *        where id is the raw ICD10 code
 */

type OnSelectParams = {
  title: string
  description: string
}

type OnSelectCallbackParams = {
  id: string
  description: string
}

type Props = {
  name?: string
  value: string
  placeholder?: string
  setConditionDiagnosisCode?: (title: string) => void
  onSelect: (onSelectParams: OnSelectCallbackParams) => void
  hasSearchDescription?: boolean
  error?: string
}

function Icd10Search(props: Props): JSX.Element {
  const {
    name = 'icd10',
    value: valueProp,
    placeholder = 'ICD10',
    setConditionDiagnosisCode,
    onSelect,
    hasSearchDescription,
    error
  } = props
  const [
    valueState,
    setValueState
  ] = useState<string>(
    valueProp
      ? formatIcd10Code(valueProp)
      : ''
  )
  const [
    isValidState,
    setIsValidState
  ] = useState<boolean>(true)

  const [
    isLoadingState,
    setIsLoadingState
  ] = useState<boolean>(false)

  const [
    resultsState,
    setResultsState
  ] = useState<
    {
      title: string
      description: string
    }[]
  >([])

  const onResultSelect = (
    data: SearchResultInternal
  ) => {
    setValueState(data.result.title)
    setIsValidState(true)

    if (setConditionDiagnosisCode) {
      setConditionDiagnosisCode(
        rawIcd10Code(
          data.result.title
        )
      )
    }

    if (
      setConditionDiagnosisCode === undefined &&
      onSelect
    ) {
      onSelect({
        id: rawIcd10Code(data.result.title),
        description: data.result.description
      })
    }
  }

  const fetchIcd10Codes = (searchData: SearchProps) => {
    const icd10 = rawIcd10Code(searchData.value)
    const query = null
    const limit = 5
    const offset = 0
    const sort = null
    const queryOverride =
      hasSearchDescription === false
        ? `id:${icd10}%`
        : `id:${icd10}%,OR desc:${searchData.value}%`
    const params = searchGenerator(
      query,
      limit,
      offset,
      sort,
      queryOverride
    )
    const url = API_URL.ICD10_SEARCH
    setIsLoadingState(true)
    setValueState(searchData.value)
    setIsValidState(false)

    api.get(url, { params }).then((response: AxiosResponse<APIResult[]>) => {
      const results = map(response.data, (icd10Code: APIResult): Result => ({
        title: formatIcd10Code(icd10Code.id),
        description: icd10Code.description
      }))
      setResultsState(results)
      setIsLoadingState(false)
    })
  }

  const onClear = () => onSelect({
    id: '',
    description: ''
  })

  const onBlur = () => {
    if (isValidState === false) {
      // handle case where input contains valid, but unselected, icd10 code
      const result: OnSelectParams | undefined = resultsState.find(
        r => rawIcd10Code(r.title) === rawIcd10Code(valueState)
      )
      if (result) {
        onSelect({
          id: result.title,
          description: result.description
        })
      } else {
        setValueState('')
        setIsValidState(true)
      }
    }
  }
  const className = `field icd10 ${error ? 'error' : ''}`

  return (
    <div className="ui grid">
      <Grid.Row columns="2">
        <Grid.Column
          width="13"
          style={ {
            paddingLeft: '8px',
            paddingRight: 0
          } }
        >
          <Search
            className={ className }
            name={ name }
            value={ valueState }
            loading={ isLoadingState }
            placeholder={ placeholder }
            results={ resultsState }
            onResultSelect={
              (
                e: React.MouseEvent<HTMLDivElement, MouseEvent>,
                data: SearchResultData
              ) => {
                onResultSelect(data as SearchResultInternal)
              }
            }
            onSearchChange={ (e, value) => fetchIcd10Codes(value as SearchProps) }
            aligned="left"
            icon={ null }
            fluid={ true }
            onBlur={ onBlur }
            clear="true"
          />
        </Grid.Column>
        <Grid.Column
          width="3"
          style={ {
            paddingLeft: '4px'
          } }
        >
          <Popup
            content="Clear ICD10 Value"
            mouseEnterDelay={ 500 }
            trigger={
              shouldRenderReskin2025
                ? (
                  <ReveleerButton
                    buttonLabel=""
                    buttonSize="small"
                    buttonType="icon"
                    id="icd10-search-clear"
                    isDisabled={ false }
                    onClick={ onClear }
                    type="button"
                  >
                    <Close />
                  </ReveleerButton>
                )
                : (
                  <Button
                    id="icd10-search-clear"
                    icon
                    onClick={ onClear }
                  >
                    <Icon name="cancel" />
                  </Button>
                )
            }
          />
        </Grid.Column>
      </Grid.Row>
    </div>
  )
}

export default Icd10Search
